import clsx from 'clsx';
import React from 'react';

import IconTheWeekend from '@components/Images/Rebranding/IconTheWeekend';
import LazyImgixImage from '@components/LazyImgixImage';
import { weekend_path } from '@routes';
import { getArticleImgSrc } from '@utils/images';

import ArticleDetails from '../ArticleDetails';
import Header from '../Header';
import PictureCaption, { PICTURE_CAPTION_CLASSES } from '../PictureCaption';
import { ThemeViewsProps } from '../interfaces';
import styles from '../styles.module.scss';
import Content from './Content';

const WeekendArticle = ({
  article: {
    addendum,
    authors,
    commentCount,
    commenters,
    freeBlurb,
    fullText,
    frontCoverPhoto,
    highlight,
    id,
    pickups,
    picture,
    pictureCaption,
    publishedAt,
    subTitle,
    takeaway,
    title,
  },
  canViewFullArticle,
  clipperProps,
  env,
  isSEOBot,
  mostPopularArticles,
  newsletter,
  relatedArticles,
  shareLink,
  subscriberCouncil,
}: ThemeViewsProps) => {
  return (
    <div className="px-16 sm:px-28">
      <div className="relative m-auto lg:max-w-[1060px] xl:max-w-container-lg">
        <div className="mb-24 flex flex-col items-center justify-between sm:mb-40 lg:flex-row lg:gap-x-72 xl:gap-x-64">
          <figure
            className="order-2 m-0 mb-48 aspect-[16/9] sm:mb-56 lg:order-1 lg:mb-0 lg:aspect-[11/10] lg:w-full lg:max-w-[490px] xl:max-w-[633px]"
            aria-labelledby="caption-text"
          >
            <LazyImgixImage
              alt={title}
              className="article-hero-image aspect-[11/10] w-full object-cover"
              src={getArticleImgSrc(id, frontCoverPhoto ?? picture, env)}
              wrapperClassName="!hidden aspect-[11/10] max-w-[48vw] lg:-ml-[calc((100vw-1060px)/2)] lg:!flex xl:-ml-[calc((100vw-78.5rem)/2)] xl:max-w-[50vw]"
            />
            <LazyImgixImage
              alt={title}
              className="article-hero-image aspect-[16/9] w-full"
              src={getArticleImgSrc(id, picture, env)}
              wrapperClassName="!flex aspect-[16/9] h-auto w-screen lg:!hidden"
            />
            <PictureCaption
              className="block px-16 sm:px-28 lg:w-full lg:px-0"
              pictureCaption={pictureCaption}
            />
          </figure>
          <div className="relative order-1 mx-auto mb-48 w-full max-w-[270px] text-[1px] sm:mb-64 sm:max-w-[350px] lg:absolute lg:right-0 lg:top-0 lg:max-w-[225px]">
            <a href={weekend_path()}>
              <IconTheWeekend aria-label="The Weekend logo" role="img" />
            </a>
          </div>
          <div className="z-10 order-3 flex-1 lg:order-1 lg:max-w-[560px]">
            <Header
              highlight={highlight}
              title={title}
              titleClassName="sm:!text-headline-lg"
              subTitle={subTitle}
              subTitleClassName="lg:text-subheadline-md xl:text-subheadline-lg"
            />
          </div>
        </div>
        <div className="flex flex-col-reverse md:flex-row md:gap-72 xl:gap-112">
          <div className="z-10 flex-1">
            <div className="mb-40 sm:mb-48 md:mb-64">
              <ArticleDetails
                authors={authors}
                commentCount={commentCount}
                commenters={commenters}
                publishedAt={publishedAt}
                shareLink={shareLink}
              />
            </div>
          </div>
          <div className={clsx(PICTURE_CAPTION_CLASSES, 'md:w-[195px] md:flex-shrink-0')} />
        </div>
        <Content
          addendum={addendum}
          authors={authors}
          bodyClassName={styles.isFeature}
          canViewFullArticle={canViewFullArticle}
          clipperProps={clipperProps}
          commentCount={commentCount}
          env={env}
          freeBlurb={freeBlurb}
          fullText={fullText}
          id={id}
          isSEOBot={isSEOBot}
          mostPopularArticles={mostPopularArticles}
          newsletter={newsletter}
          pickups={pickups}
          relatedArticles={relatedArticles}
          subscriberCouncil={subscriberCouncil}
          takeaway={takeaway}
        />
      </div>
    </div>
  );
};

export default WeekendArticle;
